import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, downloadPdf, formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileDown, Repeat, Wallet } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import EstadoPagoBadge from "@/components/EstadoPagoBadge";

const STATES_INFO = {
  PENDIENTE: { title: "Estamos preparando tu pedido", subtitle: "Te avisamos cuando esté confirmado.", color: "#A6751F", bg: "bg-[#FDF3E1]", border: "border-[#F0DEB2]" },
  CONFIRMADO: { title: "Tu pedido fue confirmado", subtitle: "Podés descargar el remito.", color: "#545848", bg: "bg-[#E9EBE4]", border: "border-[#D5D8CD]" },
  ENTREGADO: { title: "Pedido entregado", subtitle: "¡Gracias por tu compra!", color: "#3F6B3C", bg: "bg-[#DAE6D7]", border: "border-[#BCD3B7]" },
  CANCELADO: { title: "Pedido cancelado", subtitle: "Este pedido fue cancelado.", color: "#8E2E2E", bg: "bg-[#FBEBEB]", border: "border-[#F0CECE]" },
};

export default function ClienteMiPedidoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [pedido, setPedido] = useState(null);
  const [remito, setRemito] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const p = await api.get(`/pedidos/${id}`);
        setPedido(p.data);
        try {
          const r = await api.get("/remitos");
          const found = r.data.find((x) => x.pedido_id === p.data.id);
          if (found) setRemito(found);
        } catch (err) {
          console.warn("No se pudo cargar el remito asociado:", err);
        }
      } finally { setLoading(false); }
    };
    load();
  }, [id]);

  if (loading) return <div className="text-[#8C8C8C]">Cargando...</div>;
  if (!pedido) return <div className="text-[#8C8C8C]">Pedido no encontrado.</div>;

  const info = STATES_INFO[pedido.estado] || STATES_INFO.PENDIENTE;

  const descargar = async () => {
    if (!remito) return;
    try {
      await downloadPdf(`/remitos/${remito.id}/pdf`, `remito_${remito.remito_id}.pdf`);
    } catch {
      toast.error("Error al descargar PDF.");
    }
  };

  const repetirPedido = () => {
    pedido.items.forEach((it) => {
      cart.add({ id: it.producto_id, nombre: it.producto_nombre, precio: it.precio_unitario }, it.cantidad);
    });
    toast.success("Pedido cargado al carrito");
    navigate("/cliente/carrito");
  };

  return (
    <div>
      <Link to="/cliente/pedidos" className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#C85A32] mb-4" data-testid="cli-pd-back">
        <ArrowLeft className="h-4 w-4" /> Volver a mis pedidos
      </Link>

      <div className="text-[11px] uppercase tracking-[0.22em] text-[#C85A32] font-semibold mb-2 flex items-center gap-2 flex-wrap">
        Pedido <span className="font-mono">{pedido.pedido_id}</span>
        <EstadoPagoBadge estado={pedido.estado_pago} />
      </div>
      <h1 className="font-display font-bold text-3xl sm:text-5xl tracking-tight">{formatDate(pedido.fecha)}</h1>

      <div className={`mt-5 mb-6 p-4 rounded-2xl border ${info.bg} ${info.border}`} data-testid="cli-pd-status">
        <div className="font-display font-semibold text-lg" style={{ color: info.color }}>{info.title}</div>
        <div className="text-sm" style={{ color: info.color, opacity: 0.85 }}>{info.subtitle}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
          <div className="p-5 border-b border-[#E5E0D8] flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C]">Items</div>
            <div className="text-[11px] text-[#8C8C8C]">{pedido.items.length} producto{pedido.items.length !== 1 ? "s" : ""}</div>
          </div>
          <ul className="divide-y divide-[#F0EBE0]">
            {pedido.items.map((it, idx) => (
              <li key={it.id || idx} className="p-4 flex items-center gap-3" data-testid={`cli-pd-item-${idx}`}>
                <div className="h-9 w-9 rounded-md bg-[#F4EAE6] flex items-center justify-center text-[#C85A32] font-bold text-sm shrink-0">
                  {it.cantidad}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.producto_nombre}</div>
                  <div className="text-[12px] text-[#595959] font-mono">{formatMoney(it.precio_unitario)} c/u</div>
                </div>
                <div className="font-mono font-semibold">{formatMoney(it.subtotal)}</div>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="border-[#E5E0D8] rounded-2xl bg-white p-6 h-fit">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-2">Total</div>
          <div className="font-display font-bold text-4xl text-[#C85A32] mb-6" data-testid="cli-pd-total">{formatMoney(pedido.total)}</div>

          {pedido.nota && (
            <div className="mb-5 pb-5 border-b border-[#E5E0D8]">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-1">Tu nota</div>
              <div className="text-sm">{pedido.nota}</div>
            </div>
          )}

          {/* Estado de pago */}
          {(pedido.estado_pago === "PARCIAL" || pedido.estado_pago === "CUENTA_CORRIENTE") && pedido.saldo_pendiente > 0 && (
            <div className="mb-5 pb-5 border-b border-[#E5E0D8]" data-testid="cli-pd-saldo">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#A64A29] mb-1 flex items-center gap-1.5">
                <Wallet className="h-3 w-3" /> Saldo pendiente
              </div>
              <div className="font-mono font-bold text-2xl text-[#A64A29]">{formatMoney(pedido.saldo_pendiente)}</div>
              {pedido.monto_pagado > 0 && (
                <div className="text-[11px] text-[#595959] mt-0.5">
                  Ya pagaste {formatMoney(pedido.monto_pagado)}
                </div>
              )}
              <div className="text-[11px] text-[#8C8C8C] mt-1.5">
                Coordiná el pago con el negocio.
              </div>
            </div>
          )}
          {pedido.estado_pago === "PAGADO" && (
            <div className="mb-5 pb-5 border-b border-[#E5E0D8] text-[#3F6B3C] text-sm font-semibold flex items-center gap-1.5" data-testid="cli-pd-pagado">
              <Wallet className="h-4 w-4" /> Pedido pagado en su totalidad.
            </div>
          )}

          {remito && (
            <Button onClick={descargar} className="w-full h-11 bg-[#1A1A1A] hover:bg-[#000] text-white gap-2 mb-2" data-testid="cli-pd-pdf">
              <FileDown className="h-4 w-4" /> Descargar remito PDF
            </Button>
          )}

          <Button variant="outline" onClick={repetirPedido} className="w-full h-11 border-[#E5E0D8] hover:bg-[#F9F6F0] gap-2" data-testid="cli-pd-repetir">
            <Repeat className="h-4 w-4" /> Repetir este pedido
          </Button>
        </Card>
      </div>
    </div>
  );
}
